﻿import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Briefcase,
  KeyRound,
  Mail,
  Pencil,
  Phone,
  RotateCcw,
  Save,
  ShieldCheck,
  UserCircle2,
} from 'lucide-react';
import {
  ALL_ROLE_CODES,
  type ApiError,
  type ModuleCategory,
  type ModuleCode,
  type ModuleInfo,
  type RoleCode,
} from '@tracker/shared';
import { usersService, type StaffDetail } from '@/services/users.service';
import { formatDateDMY } from '@/lib/dateFormat';
import { rolesService } from '@/services/roles.service';
import { modulesService } from '@/services/modules.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const TAB_KEYS = ['profile', 'access'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  profile: 'Profile',
  access: 'Access',
};

export function StaffDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabKey) || 'profile';
  const [activeTab, setActiveTab] = useState<TabKey>(
    TAB_KEYS.includes(initialTab) ? initialTab : 'profile',
  );

  const { data, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => usersService.get(id),
    enabled: !!id,
  });

  const onTabChange = (key: string) => {
    setActiveTab(key as TabKey);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'profile') next.delete('tab');
      else next.set('tab', key);
      return next;
    });
  };

  if (isLoading || !data) {
    return <div className="text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      <Link
        to="/hrms/staff/list"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Employee Profile
      </Link>

      <StaffHeaderCard staff={data} />

      <Tabs
        tabs={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABELS[k] }))}
        active={activeTab}
        onChange={onTabChange}
      />

      <div className="space-y-3">
        {activeTab === 'profile' && <ProfileTab staff={data} />}
        {activeTab === 'access' && (
          <AccessTab staff={data} id={id} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Header card — name, code, badges, status, edit button
// ============================================================
function StaffHeaderCard({ staff }: { staff: StaffDetail }) {
  const { user, roles } = staff;
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
    <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-muted">
              {(staff.new_photos_url ?? staff.profile?.avatar_url) ? (
                <img src={(staff.new_photos_url ?? staff.profile?.avatar_url)!} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserCircle2 className="h-9 w-9 text-muted-foreground" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold uppercase tracking-tight text-primary">
                {user.first_name} {user.last_name}
              </h2>
              <div className="text-xs text-muted-foreground">{user.employee_code ?? '—'}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" /> {user.email}
                </span>
                {user.phone && (
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {user.phone}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {roles.map((r) => (
                  <Badge key={r} icon={<UserCircle2 className="h-3 w-3" />}>
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span
              className={
                user.is_active
                  ? 'inline-flex rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground'
                  : 'inline-flex rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground'
              }
            >
              {user.is_active ? 'active' : 'inactive'}
            </span>
            <Button asChild size="sm" variant="outline">
              <Link to={`/hrms/staff/${user.id}/edit`}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            </Button>
          </div>
    </div>
      </CardContent>
    </Card>
  );
}

function Badge({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
      {icon}
      {children}
    </span>
  );
}

// ============================================================
// Profile tab — Details
// ============================================================
function ProfileTab({ staff }: { staff: StaffDetail }) {
  const { user } = staff;

  return (
    <div className="space-y-4">
      <SectionCard icon={<Briefcase className="h-4 w-4" />} title="Details">
        <KVGrid
          rows={[
            ['Phone', user.phone ?? 'N/A'],
            ['Status', user.is_active ? 'Active' : 'Inactive'],
            ['Last Login', formatDateDMY(user.last_login_at)],
            ['Created', formatDateDMY(user.created_at)],
          ]}
        />
      </SectionCard>
    </div>
  );
}

function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <SectionHeader icon={icon} title={title} />
      <CardContent className="p-4">{children}</CardContent>
    </Card>
  );
}

function KVGrid({ rows }: { rows: Array<[string, string | null]> }) {
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-3 md:grid-cols-3">
      {rows.map(([label, value]) => (
        <div key={label}>
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-0.5 text-sm font-medium">{value || 'N/A'}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Access tab — port of the StaffAccessPage content
// ============================================================
const CATEGORY_LABEL: Record<ModuleCategory, string> = {
  workspace: 'Workspace',
  operations: 'Operations',
  system: 'System',
};

const CATEGORY_ORDER: ModuleCategory[] = [
  'workspace',
  'operations',
  'system',
];

function AccessTab({ staff, id }: { staff: StaffDetail; id: string }) {
  const queryClient = useQueryClient();
  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: () => rolesService.list() });
  const { data: catalog } = useQuery({
    queryKey: ['modules-catalog'],
    queryFn: () => modulesService.list(),
  });

  const [selectedRoles, setSelectedRoles] = useState<Set<RoleCode>>(new Set(staff.roles));
  const [selectedModules, setSelectedModules] = useState<Set<ModuleCode>>(new Set(staff.modules));
  const [initialModules, setInitialModules] = useState<Set<ModuleCode>>(new Set(staff.modules));
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  useEffect(() => {
    setSelectedRoles(new Set(staff.roles));
    setSelectedModules(new Set(staff.modules));
    setInitialModules(new Set(staff.modules));
  }, [staff]);

  const modulesByCategory = useMemo(() => {
    const grouped = new Map<ModuleCategory, ModuleInfo[]>();
    for (const m of catalog ?? []) {
      const arr = grouped.get(m.category) ?? [];
      arr.push(m);
      grouped.set(m.category, arr);
    }
    return grouped;
  }, [catalog]);

  const saveRoles = useMutation({
    mutationFn: () =>
      usersService.assignRoles(id, { role_codes: Array.from(selectedRoles) as RoleCode[] }),
    onSuccess: () => {
      toast.success('Roles updated. Hit "Reset to defaults" if you want modules re-seeded.');
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const saveModules = useMutation({
    mutationFn: () => {
      const add: ModuleCode[] = [];
      const remove: ModuleCode[] = [];
      for (const m of selectedModules) if (!initialModules.has(m)) add.push(m);
      for (const m of initialModules) if (!selectedModules.has(m)) remove.push(m);
      return usersService.setModules(id, { add, remove });
    },
    onSuccess: () => {
      toast.success('Module visibility saved');
      setInitialModules(new Set(selectedModules));
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: () => usersService.resetModulesToDefaults(id),
    onSuccess: async () => {
      toast.success('Modules reset to role defaults');
      const refreshed = await usersService.get(id);
      setSelectedModules(new Set(refreshed.modules));
      setInitialModules(new Set(refreshed.modules));
      void queryClient.invalidateQueries({ queryKey: ['staff', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dirtyModules =
    selectedModules.size !== initialModules.size ||
    Array.from(selectedModules).some((m) => !initialModules.has(m));

  const toggleRole = (r: RoleCode) =>
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });

  const toggleModule = (m: ModuleCode) =>
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });

  const toggleCategory = (cat: ModuleCategory) => {
    const inCat = (modulesByCategory.get(cat) ?? []).map((m) => m.code as ModuleCode);
    setSelectedModules((prev) => {
      const next = new Set(prev);
      const allOn = inCat.every((m) => next.has(m));
      if (allOn) for (const m of inCat) next.delete(m);
      else for (const m of inCat) next.add(m);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <SectionCard icon={<ShieldCheck className="h-4 w-4" />} title="Roles (Capability)">
        <p className="mb-3 text-xs text-muted-foreground">
          Pick one or more. Multi-role users get the union of all role permissions.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_ROLE_CODES.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                selectedRoles.has(r)
                  ? 'border-primary bg-primary/5'
                  : 'border-border/60 hover:bg-muted/30'
              }`}
            >
              <Checkbox checked={selectedRoles.has(r)} onChange={() => toggleRole(r)} />
              <span>{(roles ?? []).find((rr) => rr.code === r)?.name ?? r}</span>
              <span className="font-mono text-xs text-muted-foreground">{r}</span>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={() => saveRoles.mutate()} disabled={saveRoles.isPending}>
            <Save className="h-4 w-4" /> Save Roles
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        icon={<KeyRound className="h-4 w-4" />}
        title={`Module Visibility ${dirtyModules ? '(unsaved changes)' : ''}`}
      >
        <p className="mb-3 text-xs text-muted-foreground">
          Check what this user can SEE in their sidebar. This is the only place per-user module
          visibility is edited.
        </p>
        <div className="space-y-4">
          {CATEGORY_ORDER.map((cat) => {
            const mods = modulesByCategory.get(cat) ?? [];
            if (mods.length === 0) return null;
            const inCat = mods.map((m) => m.code as ModuleCode);
            const checkedInCat = inCat.filter((m) => selectedModules.has(m)).length;
            return (
              <div key={cat}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {CATEGORY_LABEL[cat]}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({checkedInCat}/{inCat.length})
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => toggleCategory(cat)}
                  >
                    Toggle all
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                  {mods.map((m) => (
                    <label
                      key={m.code}
                      className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm hover:bg-muted/30"
                    >
                      <Checkbox
                        checked={selectedModules.has(m.code as ModuleCode)}
                        onChange={() => toggleModule(m.code as ModuleCode)}
                      />
                      <span className="flex-1 truncate">{m.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setResetConfirmOpen(true)}
            disabled={reset.isPending}
          >
            <RotateCcw className="h-4 w-4" /> Reset to role defaults
          </Button>
          <Button
            size="sm"
            onClick={() => saveModules.mutate()}
            disabled={saveModules.isPending || !dirtyModules}
          >
            <Save className="h-4 w-4" /> Save Modules
          </Button>
        </div>
      </SectionCard>

      <ConfirmDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title="Reset modules to role defaults?"
        description="Unsaved changes will be lost. This will overwrite the current module selection with the role's defaults."
        confirmLabel="Reset"
        onConfirm={() => reset.mutate()}
      />

      <SectionCard
        icon={<KeyRound className="h-4 w-4" />}
        title="Permissions via Role (read-only)"
      >
        <p className="mb-2 text-xs text-muted-foreground">
          Derived from the role(s) above. To change a role's permission set, go to{' '}
          <Link to="/system/roles" className="underline">
            Master · Roles
          </Link>
          .
        </p>
        {staff.permissions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {staff.roles.includes('SUPER_ADMIN' as RoleCode)
              ? 'SUPER_ADMIN — bypasses all checks (no explicit permissions).'
              : 'No explicit permissions via current role assignments.'}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {staff.permissions.map((p) => (
              <span
                key={p}
                className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
